'use client'

import { cn } from '@/lib/utils'
import { usePathname, useSearchParams } from 'next/navigation'
import { Suspense, useCallback, useEffect, useRef, useState } from 'react'

/**
 * Gradient top bar shown during App Router navigations.
 * Starts on internal link interaction and completes when the URL updates.
 */
function NavigationProgressInner() {
    const pathname = usePathname()
    const searchParams = useSearchParams()
    const routeKey = `${pathname}?${searchParams.toString()}`

    const [progress, setProgress] = useState(0)
    const [visible, setVisible] = useState(false)

    const loadingRef = useRef(false)
    const timersRef = useRef<ReturnType<typeof setTimeout>[]>([])
    const prevRouteKeyRef = useRef<string | null>(null)
    const completeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const clearTrickle = () => {
        timersRef.current.forEach(clearTimeout)
        timersRef.current = []
    }

    const clearCompleteTimer = () => {
        if (completeTimerRef.current) {
            clearTimeout(completeTimerRef.current)
            completeTimerRef.current = null
        }
    }

    const finishBar = useCallback(() => {
        clearTrickle()
        loadingRef.current = false
        setVisible(true)
        setProgress(100)
        clearCompleteTimer()
        completeTimerRef.current = setTimeout(() => {
            setVisible(false)
            setProgress(0)
        }, 280)
    }, [])

    useEffect(() => {
        const prev = prevRouteKeyRef.current
        prevRouteKeyRef.current = routeKey

        if (prev === null || prev === routeKey) {
            return
        }

        finishBar()
    }, [routeKey, finishBar])

    const startProgress = useCallback(() => {
        clearTrickle()
        clearCompleteTimer()
        loadingRef.current = true
        setVisible(true)
        setProgress(14)
        requestAnimationFrame(() => setProgress(34))

        const push = (delay: number, value: number) => {
            const id = setTimeout(() => setProgress(value), delay)
            timersRef.current.push(id)
        }
        push(90, 50)
        push(220, 64)
        push(450, 77)
        push(700, 86)
        push(1100, 92)
    }, [])

    useEffect(() => {
        const onPointerDown = (e: PointerEvent) => {
            if (e.button !== 0) return
            const el = e.target as HTMLElement | null
            const anchor = el?.closest?.('a[href]')
            if (!anchor) return
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
            if (anchor.target === '_blank' || anchor.hasAttribute('download')) return

            const href = anchor.getAttribute('href')
            if (!href || href.startsWith('#')) return

            let url: URL
            try {
                url = new URL(href, window.location.origin)
            } catch {
                return
            }
            if (url.origin !== window.location.origin) return

            const next = url.pathname + url.search
            const current = window.location.pathname + window.location.search
            if (next === current) return

            startProgress()
        }

        document.addEventListener('pointerdown', onPointerDown, true)
        return () => document.removeEventListener('pointerdown', onPointerDown, true)
    }, [startProgress])

    useEffect(() => {
        if (!visible || !loadingRef.current) return
        const failSafe = setTimeout(() => {
            if (!loadingRef.current) return
            loadingRef.current = false
            clearTrickle()
            setVisible(false)
            setProgress(0)
        }, 12000)
        return () => clearTimeout(failSafe)
    }, [visible, routeKey])

    return (
        <div
            aria-hidden
            dir="ltr"
            className={cn(
                'pointer-events-none fixed inset-x-0 top-0 z-[9999] h-[2px] transition-opacity duration-200',
                visible ? 'opacity-100' : 'opacity-0',
            )}>
            <div
                className={cn(
                    'h-full bg-gradient-to-r from-primary via-indigo-400 to-cyan-400 shadow-[0_0_14px_rgba(65,91,204,0.35)] transition-[width] duration-300 ease-out',
                )}
                style={{ width: `${progress}%` }}
            />
        </div>
    )
}

export function NavigationProgress() {
    return (
        <Suspense fallback={null}>
            <NavigationProgressInner />
        </Suspense>
    )
}
